/**
 * "Edit image", as in Discord: the picked image under a circular (avatar)
 * or rounded-square (icon) mask. Drag to move it; zoom with the slider,
 * the wheel or a pinch; Reset; Apply or Cancel. Keyboard: arrow keys move
 * (Shift for bigger steps), + and - zoom, 0 resets, Enter applies, Escape
 * cancels. Any size of image opens: it is decoded shrunk in the image
 * worker first ("Optimizing…"), and the crop is re-encoded there too.
 *
 * Styles: src/styles/image.css (ns-crop-*), kept apart for the redesign.
 */
import type { ImagePurpose } from '@neosocial/protocol';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as RKeyboardEvent,
  type PointerEvent as RPointerEvent,
} from 'react';
import { useT } from '../i18n';
import {
  encodeCropped,
  imageErrorText,
  isAbort,
  prepareImage,
  stillText,
  type EncodedImage,
} from '../lib/image';
import {
  cropRect,
  drawRect,
  INITIAL_CROP,
  maskSize,
  panBy,
  zoomTo,
  type CropGeometry,
  type CropState,
} from '../lib/image/crop';
import { MAX_ZOOM, PRESETS, type Size } from '../lib/image/plan';
import { Btn, Icon } from './kit';
import { Dialog, toast } from './overlay';

type EditPurpose = Extract<ImagePurpose, 'avatar' | 'icon' | 'banner' | 'event_cover'>;

const STEP = 12;
const ZOOM_STEP = 1.12;

export function ImageEditor({
  file,
  purpose,
  onDone,
  onCancel,
}: {
  file: File;
  purpose: EditPurpose;
  onDone(img: EncodedImage): void;
  onCancel(): void;
}) {
  const { t, fmt } = useT();
  const preset = PRESETS[purpose];
  const aspect = preset.aspect ?? 1;
  const [bitmap, setBitmap] = useState<ImageBitmap | null>(null);
  const [progress, setProgress] = useState(0);
  const [crop, setCrop] = useState<CropState>(INITIAL_CROP);
  const [stage, setStage] = useState<Size>({ width: 0, height: 0 });
  const [applying, setApplying] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ dist: number; zoom: number } | null>(null);
  const done = useRef({ onDone, onCancel, t });
  useEffect(() => {
    done.current = { onDone, onCancel, t };
  });

  // Decode (shrunk, upright) in the worker; the bitmap is ours until unmount.
  useEffect(() => {
    const ctl = new AbortController();
    let mine: ImageBitmap | null = null;
    prepareImage(file, purpose, { signal: ctl.signal, onProgress: setProgress }).then(
      (r) => {
        if (ctl.signal.aborted) {
          r.bitmap.close();
          return;
        }
        mine = r.bitmap;
        setBitmap(r.bitmap);
        if (r.still) toast(stillText(done.current.t, r.still), { icon: 'image' });
      },
      (e: unknown) => {
        if (ctl.signal.aborted || isAbort(e)) return;
        toast(imageErrorText(done.current.t, e), { icon: 'image', tone: 'warn', duration: 7000 });
        done.current.onCancel();
      },
    );
    return () => {
      ctl.abort();
      mine?.close();
    };
  }, [file, purpose]);

  // The stage's size decides the mask's.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() =>
      setStage({ width: el.clientWidth, height: el.clientHeight }),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const mask = useMemo(
    () => maskSize(stage, aspect, Math.round(Math.min(stage.width, stage.height) * 0.08)),
    [stage, aspect],
  );
  const geo = useMemo<CropGeometry | null>(
    () =>
      bitmap && stage.width
        ? { image: { width: bitmap.width, height: bitmap.height }, mask }
        : null,
    [bitmap, mask, stage.width],
  );

  // Draw.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !geo || !bitmap) return;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    c.width = Math.round(stage.width * dpr);
    c.height = Math.round(stage.height * dpr);
    const g = c.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, stage.width, stage.height);
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    const r = drawRect(geo, crop, stage);
    g.drawImage(bitmap, r.dx, r.dy, r.dw, r.dh);
  }, [geo, bitmap, crop, stage]);

  // The wheel needs a non-passive listener to keep the page from scrolling.
  useEffect(() => {
    const el = stageRef.current;
    if (!el || !geo) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const b = el.getBoundingClientRect();
      const at = { x: e.clientX - b.left - b.width / 2, y: e.clientY - b.top - b.height / 2 };
      const k = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0016));
      setCrop((c) => zoomTo(geo, c, c.zoom * k, at));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [geo]);

  const ready = !!geo && !applying;

  // Cancel (or closing the dialog) while applying stops the encode, quietly.
  const applying$ = useRef<AbortController | null>(null);
  useEffect(() => () => applying$.current?.abort(), []);

  const apply = async () => {
    if (!geo || !bitmap || applying) return;
    setApplying(true);
    const ctl = new AbortController();
    applying$.current = ctl;
    try {
      const out = await encodeCropped(bitmap, purpose, cropRect(geo, crop), {
        signal: ctl.signal,
      });
      if (!ctl.signal.aborted) done.current.onDone(out);
    } catch (e) {
      if (ctl.signal.aborted || isAbort(e)) return;
      setApplying(false);
      toast(imageErrorText(t, e), { icon: 'image', tone: 'warn' });
    }
  };

  const local = (e: { clientX: number; clientY: number }) => {
    const b = stageRef.current!.getBoundingClientRect();
    return { x: e.clientX - b.left - b.width / 2, y: e.clientY - b.top - b.height / 2 };
  };

  const onPointerDown = (e: RPointerEvent<HTMLDivElement>) => {
    if (!geo) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a!.x - b!.x, a!.y - b!.y) || 1, zoom: crop.zoom };
    }
  };
  const onPointerMove = (e: RPointerEvent<HTMLDivElement>) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev || !geo) return;
    const now = { x: e.clientX, y: e.clientY };
    pointers.current.set(e.pointerId, now);
    if (pointers.current.size === 1) {
      setCrop((c) => panBy(geo, c, now.x - prev.x, now.y - prev.y));
    } else if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      const mid = local({ clientX: (a!.x + b!.x) / 2, clientY: (a!.y + b!.y) / 2 });
      const z = (pinch.current.zoom * dist) / pinch.current.dist;
      setCrop((c) => zoomTo(geo, c, z, mid));
    }
  };
  const onPointerEnd = (e: RPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  };

  const onKeyDown = (e: RKeyboardEvent<HTMLDivElement>) => {
    if (!geo) return;
    const big = e.shiftKey ? 4 : 1;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-STEP * big, 0],
      ArrowRight: [STEP * big, 0],
      ArrowUp: [0, -STEP * big],
      ArrowDown: [0, STEP * big],
    };
    const m = moves[e.code];
    if (m) setCrop((c) => panBy(geo, c, m[0], m[1]));
    else if (e.code === 'Equal' || e.code === 'NumpadAdd')
      setCrop((c) => zoomTo(geo, c, c.zoom * ZOOM_STEP));
    else if (e.code === 'Minus' || e.code === 'NumpadSubtract')
      setCrop((c) => zoomTo(geo, c, c.zoom / ZOOM_STEP));
    else if (e.code === 'Digit0' || e.code === 'Numpad0') setCrop(INITIAL_CROP);
    else if (e.code === 'Enter' || e.code === 'NumpadEnter') void apply();
    else return;
    e.preventDefault();
  };

  const pct = Math.round(progress * 100);
  return (
    <Dialog
      title={t('img.edit.title')}
      cls="ns-crop-dlg"
      width={460}
      onClose={onCancel}
      initialFocus={stageRef}
      actions={
        <>
          <Btn
            kind="quiet"
            size="sm"
            className="ns-crop-reset"
            disabled={!ready || (crop.zoom === 1 && crop.x === 0 && crop.y === 0)}
            onClick={() => setCrop(INITIAL_CROP)}
          >
            {t('img.edit.reset')}
          </Btn>
          <Btn size="sm" onClick={onCancel}>
            {t('c.cancel')}
          </Btn>
          <Btn
            kind="primary"
            size="sm"
            disabled={!ready}
            busy={applying}
            onClick={() => void apply()}
          >
            {t('img.edit.apply')}
          </Btn>
        </>
      }
    >
      <div
        className="ns-crop"
        data-shape={preset.mask ?? 'rect'}
        data-aspect={aspect === 1 ? 'square' : 'wide'}
      >
        <div
          ref={stageRef}
          className="ns-crop-stage"
          tabIndex={0}
          role="group"
          aria-roledescription={t('img.edit.stageRole')}
          aria-label={t('img.edit.stage')}
          aria-busy={!geo || undefined}
          data-ready={geo ? '' : undefined}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          onKeyDown={onKeyDown}
        >
          <canvas ref={canvasRef} className="ns-crop-canvas" aria-hidden="true" />
          {geo ? (
            <div
              className="ns-crop-mask"
              aria-hidden="true"
              style={{ width: mask.width, height: mask.height }}
            />
          ) : (
            <div className="ns-crop-busy" role="status">
              <span>{t('img.optimizing', { n: pct })}</span>
              <span className="ns-prog" aria-hidden="true">
                <i style={{ width: `${pct}%` }} />
              </span>
            </div>
          )}
        </div>
        <div className="ns-crop-zoom">
          <Icon name="image" cls="sm" />
          <input
            type="range"
            min={1}
            max={MAX_ZOOM}
            step={0.01}
            value={crop.zoom}
            disabled={!ready}
            aria-label={t('img.edit.zoom')}
            aria-valuetext={fmt.num(Math.round(crop.zoom * 100)) + '%'}
            onChange={(e) => {
              const z = Number(e.target.value);
              if (geo) setCrop((c) => zoomTo(geo, c, z));
            }}
          />
          <Icon name="image" cls="lg" />
        </div>
        <p className="ns-crop-help">{t('img.edit.help')}</p>
      </div>
    </Dialog>
  );
}
